import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useProjectMemory } from "@/hooks/useProjectMemory";
import IntelligenceMarkdown from "../IntelligenceMarkdown";
import { ChevronDown, ChevronRight, User } from "lucide-react";

interface OverviewTabProps {
  projectId: string;
  project: any;
  onProjectUpdate?: (project: any) => void;
}

interface Section {
  id: string;
  title: string;
  content: string;
  order: number;
}

export default function OverviewTab({ projectId, project, onProjectUpdate }: OverviewTabProps) {
  const { backendToken } = useAuth();
  const { data: memory } = useProjectMemory(projectId);
  const [sections, setSections] = useState<Section[]>([]);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  // Define section order and titles
  const sectionConfig = [
    { id: "executive_summary", title: "Executive Summary", order: 1, key: "discovery_summary" },
    { id: "scope_objectives", title: "Project Scope & Objectives", order: 2, key: "discovery_scope_objectives" },
    { id: "client_stakeholders", title: "Client & Stakeholder Information", order: 3, key: "discovery_client_stakeholders" },
    { id: "financial_performance", title: "Financial Performance", order: 4, key: "discovery_financial_performance" },
    { id: "team_resources", title: "Project Team & Resources", order: 5, key: "discovery_team_resources" },
    { id: "document_inventory", title: "Document Classification & Inventory", order: 6, key: "discovery_document_inventory" },
    { id: "entities_relationships", title: "Key Entities & Relationships", order: 7, key: "discovery_entities_relationships" },
  ];

  useEffect(() => {
    // Listen for scrollToSection events from other components
    const handleScrollToSection = (e: CustomEvent) => {
      const sectionId = e.detail?.sectionId;
      if (sectionId) {
        scrollToSection(sectionId);
      }
    };

    window.addEventListener('scrollToSection', handleScrollToSection as EventListener);
    return () => {
      window.removeEventListener('scrollToSection', handleScrollToSection as EventListener);
    };
  }, []);

  useEffect(() => {
    const loadOverview = async () => {
      const token = backendToken;
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        // Build sections from individual columns
        const loadedSections: Section[] = [];

        for (const config of sectionConfig) {
          const content = project?.[config.key];
          if (content && content.trim()) {
            loadedSections.push({
              id: config.id,
              title: config.title,
              content: content,
              order: config.order,
            });
            // Expand first section (Executive Summary) by default
            if (config.id === "executive_summary") {
              setExpandedSections(prev => new Set(prev).add(config.id));
            }
          }
        }

        // If no individual columns, fallback to legacy format
        if (loadedSections.length === 0) {
          if (project?.discovery_sections && typeof project.discovery_sections === 'object') {
            const sections = project.discovery_sections;
            let order = 1;
            for (const [key, value] of Object.entries(sections)) {
              const sectionData = value as any;
              const content = sectionData.body || sectionData.content || sectionData.text || "";
              if (content) {
                loadedSections.push({
                  id: key,
                  title: sectionData.title || key,
                  content: content,
                  order: order++,
                });
              }
            }
            // Add executive summary if available
            if (project.discovery_summary) {
              loadedSections.unshift({
                id: "executive_summary",
                title: "Executive Summary",
                content: project.discovery_summary,
                order: 1,
              });
            }
            // Expand first section by default
            if (loadedSections.length > 0) {
              setExpandedSections(new Set([loadedSections[0].id]));
            }
          } else if (project?.discovery_summary) {
            loadedSections.push({
              id: "executive_summary",
              title: "Executive Summary",
              content: project.discovery_summary,
              order: 1,
            });
            setExpandedSections(new Set(["executive_summary"]));
          }
        }

        // If still empty, use project_memory scope_summary as fallback
        if (loadedSections.length === 0 && memory?.scope_summary) {
          loadedSections.push({
            id: "project_scope",
            title: "Project Overview",
            content: memory.scope_summary,
            order: 1,
          });
          setExpandedSections(new Set(["project_scope"]));
        }

        setSections(loadedSections);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };

    if (projectId && project) {
      loadOverview();
    }
  }, [projectId, backendToken, project, memory?.scope_summary]);

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(`section-${sectionId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Expand section if collapsed
      if (!expandedSections.has(sectionId)) {
        setExpandedSections(prev => new Set(prev).add(sectionId));
      }
    }
  };

  const keyPeople = memory?.key_people || [];

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-2" style={{ borderColor: "var(--accent)" }}></div>
          <p className="text-xs" style={{ color: "var(--text-primary)" }}>Loading overview...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
      <div className="flex-1 overflow-y-auto p-6 min-h-0">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Discovery Sections */}
          {sections.length > 0 ? (
            <div className="space-y-6">
              {sections.map((section) => {
                const isExpanded = expandedSections.has(section.id);
                return (
                  <div
                    key={section.id}
                    id={`section-${section.id}`}
                  >
                    <button
                      onClick={() => toggleSection(section.id)}
                      className="w-full flex items-center gap-3 text-left py-3 transition-colors group"
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "var(--secondary)"}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                    >
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 flex-shrink-0" style={{ color: "var(--text-secondary)" }} />
                      ) : (
                        <ChevronRight className="w-5 h-5 flex-shrink-0" style={{ color: "var(--text-secondary)" }} />
                      )}
                      <span className="text-lg font-semibold" style={{ color: "var(--text-primary)" }}>
                        {section.title}
                      </span>
                    </button>
                    {isExpanded && (
                      <div className="pl-8 pb-4">
                        <IntelligenceMarkdown content={section.content} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-state">
              <p style={{ color: "var(--text-secondary)" }}>No overview content available</p>
              <p className="text-sm mt-1" style={{ color: "var(--muted-foreground)" }}>The intelligence agent will populate this after syncing.</p>
            </div>
          )}

          {/* People -- from project_memory */}
          {keyPeople.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-4" style={{ color: "var(--text-primary)" }}>
                People
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {keyPeople.map((person, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3 rounded-lg p-3"
                    style={{
                      backgroundColor: "var(--surface)",
                      border: "1px solid var(--border)",
                    }}
                  >
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{
                        backgroundColor: "color-mix(in srgb, var(--accent) 12%, transparent)",
                        color: "var(--accent)",
                      }}
                    >
                      <User className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate" style={{ color: "var(--text-primary)" }}>
                        {person.name}
                      </div>
                      {person.role && (
                        <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
                          {person.role}
                        </div>
                      )}
                      {person.org && (
                        <div className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                          {person.org}
                        </div>
                      )}
                      {person.email && (
                        <div className="text-xs truncate" style={{ color: "var(--muted-foreground)" }}>
                          {person.email}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Export function for scrolling to sections (used by SourcesTab)
export const scrollToOverviewSection = (sectionId: string) => {
  const element = document.getElementById(`section-${sectionId}`);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};
